/**
 * Encodes surface selection and chat-open state as URL search params.
 *
 * Every chat-capable route reads and writes through this one place so the two
 * concerns stay consistent across host boundaries. Every write replaces the
 * current history entry so Back still leaves the page rather than undoing a
 * pane toggle.
 *
 * Params are tracked locally and written via the native history API rather
 * than react-router's useSearchParams. Both hosts also change the pathname
 * (a new artifact id, a minted conversation id) through raw
 * window.history.replaceState -- see showArtifact / handleConversationChange
 * -- to dodge the remount a router-driven pathname change would cause
 * (RouterWithErrorBoundary keys on it). react-router's own location state
 * does not observe that write, so a subsequent useSearchParams write would
 * resolve against the stale pathname it still has cached and silently
 * revert the URL. Reading and writing window.location directly keeps this
 * hook correct regardless of which mechanism last touched the pathname.
 *
 * userRole and other non-serialisable props are not encoded; callers that need
 * them enrich the returned surface descriptor themselves.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  RESOURCE_SCOPES,
  DEFAULT_RESOURCE_SCOPE,
} from '@/components/scope-tabs';
import type { SurfaceDescriptor } from '@/components/surface-host/surface-host';
import { RUN_ID_PARAM } from '@/constants/workflow-editor';
import { ARTIFACT_SORT_KEYS, DEFAULT_ARTIFACT_SORT } from '@/types/artifacts';

/**
 * Search param keys this encoder owns. The standalone gallery page reuses
 * `workflow` as its own filter, unrelated to a surface -- callers that carry
 * a location's search across to a different route (e.g. opening an artifact
 * card into `/artifacts/:id`) must strip these first, or a gallery filter is
 * misread as a layered surface on arrival.
 *
 * `runId` is the same key the standalone editor deep-links a run with, so a
 * pane opened on a run reads `?workflow=<id>&runId=<run>`, mirroring
 * `/workflow/<id>?runId=<run>`.
 *
 * `galleryScope` and `gallerySort` are deliberately distinct from the
 * standalone gallery's params: those survive a trip to `/artifacts/:id` and
 * back, while these belong to a pane that trip is leaving behind.
 */
export const SURFACE_SEARCH_PARAM_KEYS = [
  'workflow',
  RUN_ID_PARAM,
  'artifact',
  'gallery',
  'galleryLocked',
  'galleryScope',
  'gallerySort',
  'chat',
] as const;

export interface WorkspaceSurfaceDefaults {
  /**
   * Whether the chat rail opens by default when no explicit ?chat= param is
   * present. Chat-home defaults to true (chat is the point); the artifact
   * viewer defaults to false (the artifact is the point).
   */
  chatOpenByDefault?: boolean;
}

export interface WorkspaceSurfaceState {
  /** The surface currently encoded in the URL, or null. */
  surface: SurfaceDescriptor | null;
  /** Whether the chat rail is open per the URL, using the route default when absent. */
  chatOpen: boolean;
  /**
   * The run named in `?runId=`, for hosts whose workflow lives in the path
   * rather than a surface param. Always null when no `?runId=` is present.
   * Reading through the hook rather than `useLocation` matters: `writeParams`
   * goes through `window.history.replaceState`, which react-router's location
   * never observes.
   */
  runId: string | null;
  /** Replace the surface in the URL, clearing the other surface params. */
  setSurface: (descriptor: SurfaceDescriptor | null) => void;
  /** Explicitly record the chat rail's open/closed state in the URL. */
  setChatOpen: (open: boolean) => void;
  /**
   * Write or clear `?runId=` without implying a `?workflow=` surface. Use on
   * routes whose workflow is the path anchor and `?runId=` is a deep-link
   * into a specific run of that workflow.
   */
  setRunId: (runId: string | null) => void;
}

/**
 * Read and write the workspace's surface descriptor and chat-open state from
 * the current URL search params.
 *
 * Mutual exclusivity is enforced here: ?workflow= wins over ?artifact= when
 * both are present (guards against hand-edited URLs), and setSurface clears
 * all surface params before setting the new one.
 */
export function useWorkspaceSurface(
  defaults: WorkspaceSurfaceDefaults = {},
): WorkspaceSurfaceState {
  const { chatOpenByDefault = true } = defaults;
  // Reads go through react-router's own location -- the one react-router
  // navigation (a different route, Back/Forward) does update it, and it is
  // what a MemoryRouter-driven test controls, unlike window.location.
  const location = useLocation();

  const [params, setParams] = useState(
    () => new URLSearchParams(location.search),
  );

  // Resync to a real router navigation so a route change starts from the URL
  // it actually landed on rather than this hook's last write. Never fires
  // off writeParams's own writes below -- those go through window.history
  // directly, which react-router's location does not observe.
  useEffect(() => {
    setParams(new URLSearchParams(location.search));
  }, [location.pathname, location.search]);

  const writeParams = useCallback(
    (updater: (prev: URLSearchParams) => URLSearchParams) => {
      setParams((prev) => {
        const next = updater(new URLSearchParams(prev));
        const search = next.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${search ? `?${search}` : ''}`,
        );
        return next;
      });
    },
    [],
  );

  const workflowParam = params.get('workflow');
  const runIdParam = params.get(RUN_ID_PARAM);
  const artifactParam = params.get('artifact');
  const galleryParam = params.get('gallery');
  const galleryLockedParam = params.get('galleryLocked');
  const galleryScopeParam = params.get('galleryScope');
  const gallerySortParam = params.get('gallerySort');
  const chatParam = params.get('chat');

  // Mutual exclusivity: workflow > artifact > gallery when multiple present.
  // A run only means anything on a workflow surface, so it is read there
  // alone and ignored beside the other kinds.
  const surface: SurfaceDescriptor | null =
    workflowParam !== null
      ? {
          kind: 'workflow',
          workflowId: workflowParam,
          runId: runIdParam ?? undefined,
        }
      : artifactParam !== null
        ? { kind: 'artifact', artifactId: artifactParam }
        : galleryParam !== null
          ? {
              kind: 'artifact_gallery',
              workflowId: galleryParam || undefined,
              locked: galleryLockedParam === '1',
              scope: RESOURCE_SCOPES.find((key) => key === galleryScopeParam),
              sort: ARTIFACT_SORT_KEYS.find((key) => key === gallerySortParam),
            }
          : null;

  // Absent = route default; explicit 0/1 overrides it.
  const chatOpen = chatParam !== null ? chatParam === '1' : chatOpenByDefault;

  const setSurface = useCallback(
    (descriptor: SurfaceDescriptor | null) => {
      writeParams((prev) => {
        const next = new URLSearchParams(prev);
        // A bare ?runId= with no ?workflow= present belongs to the anchor
        // route (see setRunId), not to a layered surface -- only clear it
        // here when a layered workflow surface (which owns runId while it's
        // showing) is the thing being replaced.
        const hadWorkflowSurface = next.has('workflow');
        next.delete('workflow');
        next.delete(RUN_ID_PARAM);
        next.delete('artifact');
        next.delete('gallery');
        next.delete('galleryLocked');
        next.delete('galleryScope');
        next.delete('gallerySort');
        if (descriptor?.kind === 'workflow') {
          next.set('workflow', descriptor.workflowId);
          if (descriptor.runId) {
            next.set(RUN_ID_PARAM, descriptor.runId);
          } else {
            next.delete(RUN_ID_PARAM);
          }
        } else if (descriptor?.kind === 'artifact') {
          next.set('artifact', descriptor.artifactId);
          if (hadWorkflowSurface) next.delete(RUN_ID_PARAM);
        } else if (descriptor?.kind === 'artifact_gallery') {
          next.set('gallery', descriptor.workflowId ?? '');
          if (descriptor.locked) {
            next.set('galleryLocked', '1');
          }
          // DEFAULT_RESOURCE_SCOPE is what the reader falls back to, so
          // writing it would only add a param that says what its absence
          // already does.
          if (descriptor.scope && descriptor.scope !== DEFAULT_RESOURCE_SCOPE) {
            next.set('galleryScope', descriptor.scope);
          }
          if (descriptor.sort && descriptor.sort !== DEFAULT_ARTIFACT_SORT) {
            next.set('gallerySort', descriptor.sort);
          }
          if (hadWorkflowSurface) next.delete(RUN_ID_PARAM);
        } else if (hadWorkflowSurface) {
          next.delete(RUN_ID_PARAM);
        }
        return next;
      });
    },
    [writeParams],
  );

  const setChatOpen = useCallback(
    (open: boolean) => {
      writeParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('chat', open ? '1' : '0');
        return next;
      });
    },
    [writeParams],
  );

  const setRunId = useCallback(
    (runId: string | null) => {
      writeParams((prev) => {
        const next = new URLSearchParams(prev);
        if (runId) {
          next.set(RUN_ID_PARAM, runId);
        } else {
          next.delete(RUN_ID_PARAM);
        }
        return next;
      });
    },
    [writeParams],
  );

  return {
    surface,
    chatOpen,
    runId: runIdParam,
    setSurface,
    setChatOpen,
    setRunId,
  };
}
